import React, { useEffect, useState } from "react";
import useAxios from "../../../hooks/useAxios";
import { Card, message, Modal, Table } from "antd";
import { ReactLoading } from "../../Dashboard/TinManagement/import";
import {
  Accepted,
  Pending,
} from "../../../components/components/StatusIndicators";
import useGetFile from "../../../hooks/useGetFile";
import useAuthCandidateToken from "../../../hooks/useAuthCandidateToken";
import { formatDateDDMMYYYY } from "../../../utils/commonFunctions";
import useAuthUserToken from "../../../hooks/useAuthUserToken";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";

const OfferLetter = ({
  application,
  loadApplication,
  offerLetterSended,
  filledForms,
  getAllJoiningFilledForms,
  tokenType,
}) => {
  const [load, setLoad] = useState(false);
  const [rows, setRows] = useState([]);
  const [pdfUrl, setPdfUrl] = useState("");
  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [accept, setAccept] = useState(false);
  const { candidateToken } = useAuthCandidateToken();
  const { userToken } = useAuthUserToken();
  const { getImage, imageLoad } = useGetFile(
    tokenType === "user" ? userToken : candidateToken
  );
  const api = useAxios();

  const MemoPdf = React.useMemo(() => {
    if (!pdfUrl) return null;

    return (
      <DocViewer
        key={pdfUrl}
        documents={[
          {
            uri: pdfUrl,
            fileType: "pdf",
            fileName: "Offer Letter.pdf",
          },
        ]}
        config={{
          pdfVerticalScrollByDefault: true,
          header: {
            disableHeader: true,
          },
        }}
        pluginRenderers={DocViewerRenderers}
        style={{ width: "100%" }}
      />
    );
  }, [pdfUrl]);

  const consentColumns = [
    {
      title: "Form Name",
      dataIndex: "form_name",
      key: "form_name",
    },
    {
      title: "Accepted On",
      dataIndex: "accepted_on",
      key: "accepted_on",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      fixed: "right",
      render: (val, record) => {
        const { action } = record;
        return (
          <>
            <button
              style={{ flex: 1 }}
              className="btn-sm btn-primary w-100"
              onClick={async (e) => {
                e.preventDefault();
                if (
                  (action?.isFilledStatus && action?.isPdfExist) ||
                  tokenType === "user"
                ) {
                  const pdfFile = await getImage(action?.filePath);

                  setPdfUrl(pdfFile);
                  setOpenPdfModal(true);

                  return;
                }

                viewOfferLetter();
              }}
            >
              View Offer Letter
            </button>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (!offerLetterSended || filledForms?.length === 0) return;
    let data = null;
    const userType = offerLetterSended?.userType;

    if (userType === "GIG") {
      data = filledForms?.find(
        (form) => form?.companyDocumentId?.formType === "OfferLetter-GIG"
      );
    } else if (userType === "NAPS" || userType === "NATS") {
      data = filledForms?.find(
        (form) => form?.companyDocumentId?.formType === "OfferLetter-NAP-NATS"
      );
    }

    if (!data) return;

    setRows([
      {
        form_name: data?.companyDocumentId?.formName,
        status: application?.offerLetterAccepted ? <Accepted /> : <Pending />,
        action: data,
        accepted_on: application?.offerLetterAccepted
          ? formatDateDDMMYYYY(new Date(data?.updatedAt))
          : "Not Accepted",
      },
    ]);
  }, [application, offerLetterSended, filledForms]);

  const viewOfferLetter = async () => {
    try {
      setLoad(true);
      let url = "";
      let payload = {};
      const userType = offerLetterSended?.userType;
      if (userType === "GIG") {
        url = `/companyDocuments/htmltoPdfGigOfferLetter/${application?._id}`;
        payload = {
          hrSignature: offerLetterSended?.hrSignature,
          client: "digital hiring",
          DOJ: offerLetterSended?.DOJ,
          location: offerLetterSended?.location,
          ref_number: offerLetterSended?.ref_number,
        };
      } else if (userType === "NAPS" || userType === "NATS") {
        url = `/companyDocuments/htmltoPdfNAPNATSOfferLetter/${application?._id}`;
        payload = {
          hrSignature: offerLetterSended?.hrSignature,
          client: "digital hiring",
          DOJ: offerLetterSended?.DOJ,
          traningPeriod: offerLetterSended?.traningPeriod,
          location: offerLetterSended?.location,
          stipendPerMonth: offerLetterSended?.stipendPerMonth,
          candidateAddress: offerLetterSended?.candidateAddress,
          ref_number: offerLetterSended?.ref_number,
        };
      }

      const { data } = await api.post(url, payload, { tokenType });

      setPdfUrl(data?.url || "");
      await getAllJoiningFilledForms();
      setOpenPdfModal(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoad(false);
    }
  };

  const acceptOfferLetter = async (e) => {
    e.preventDefault();
    try {
      setLoad(true);
      await api.put(
        `/candidate/acceptOfferLetter/${application?._id}`,
        {
          offerLetterAccepted: true,
          companyDocumentMappingId: rows?.[0]?.action?._id || null,
        },
        { tokenType }
      );
      await Promise.all([loadApplication(), getAllJoiningFilledForms()]);
      closePdfModal();
      message.success("Offer letter accepted successfully");
    } catch (err) {
      console.error(err);
    } finally {
      setLoad(false);
    }
  };

  const closePdfModal = () => {
    setOpenPdfModal(false);
  };

  const userType = offerLetterSended?.userType;

  return (
    <>
      {(load || imageLoad) && (
        <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
      )}

      {userType === "GIG" && (
        <Card className={"mb-4"} title="Offer Letter">
          <div style={{ width: "100%" }}>
            <Table
              columns={consentColumns}
              dataSource={rows}
              pagination={false}
              scroll={{ x: true }}
              style={{ whiteSpace: "pre" }}
              size="small"
            />
          </div>
        </Card>
      )}

      {(userType === "NAPS" || userType === "NATS") && (
        <Card className={"mb-4"} title="Offer Letter">
          <div style={{ width: "100%" }}>
            <Table
              columns={consentColumns}
              dataSource={rows}
              pagination={false}
              scroll={{ x: true }}
              style={{ whiteSpace: "pre" }}
              size="small"
            />
          </div>
        </Card>
      )}

      {userType === "Normal" && (
        <Card className={"mb-4"} title="Offer Letter Acceptance">
          <div
            className="p-2 flex gap-2"
            style={{
              alignItems: "start",
            }}
          >
            <input
              onChange={acceptOfferLetter}
              style={{ transform: "scale(1.2)", marginTop: "6px" }}
              id="offer_letter"
              type="checkbox"
              disabled={application?.offerLetterAccepted}
              checked={application?.offerLetterAccepted}
            />
            <label className="mb-0" htmlFor="offer_letter">
              I acknowledge that I have received and read my offer letter and
              fully understand the terms and conditions mentioned therein, I
              confirm that:
            </label>
          </div>
          <div className="pl-2 pr-2 pb-2">
            <ol className="pl-[2.8rem]" style={{ listStyleType: "disc" }}>
              <li>
                I have <strong>received, reviewed, and understood</strong> the
                contents of my offer letter.
              </li>
              <li>
                <strong>
                  I agree to the terms, conditions, and employment details
                </strong>{" "}
                mentioned in the offer letter, including compensation, role, and
                reporting structure.
              </li>
              <li>
                I understand that proceeding further implies my{" "}
                <strong>acceptance of the offer</strong> and its associated
                terms.
              </li>
            </ol>
          </div>
        </Card>
      )}

      <Modal
        title="Offer Letter"
        centered
        open={openPdfModal}
        onOk={() => closePdfModal()}
        onCancel={(e) => {
          closePdfModal();
        }}
        afterClose={() => { 
          setPdfUrl("") 
          setAccept(false)
        }}
        width={1000}
        footer={
          <>
            {tokenType === "candidate" && (
              <>
                {application?.offerLetterAccepted ? (
                  <>
                    <div className="mt-2 d-flex gap-1 align-items-center flex-column justify-content-center">
                      <strong className="text-success">
                        You have accepted the offer letter.
                      </strong>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-2 d-flex gap-1 align-items-center flex-column justify-content-center">
                      <div
                        className="d-flex"
                        style={{
                          gap: "0.5rem",
                          width: "100%",
                          alignItems: "start",
                          paddingLeft: "25px",
                        }}
                      >
                        <input
                          id="accept"
                          type="checkbox"
                          style={{ marginTop: "5px" }}
                          value={accept}
                          checked={accept}
                          onChange={(e) => setAccept(!accept)}
                        />
                        <label
                          style={{ textAlign: "left" }}
                          className="mb-0"
                          htmlFor="accept"
                        >
                          <strong>
                            By accepting these terms and conditions, you confirm
                            that you have read and agree to abide by the terms
                            outlined above, digitally signing the agreement.
                          </strong>
                        </label>
                      </div>
                      <button
                        disabled={!accept}
                        className={`btn ${
                          accept ? "btn-primary" : "btn-secondary"
                        } mt-2`}
                        onClick={(e) => acceptOfferLetter(e)}
                      >
                        Accept
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        }
        bodyStyle={{
          height: "70vh",
          overflow: "auto",
          padding: 0,
        }}
      >
        <div className="w-100">
          {/* <iframe
            style={{ width: "100%" }}
            title="iframe"
            className="iframe"
            width="100%"
            height="600"
            src={pdfUrl}
            allow="fullscreen; autoplay; clipboard-read; clipboard-write"
          >
            <p>iframes are not supported by your browser.</p>
          </iframe> */}
          {openPdfModal && MemoPdf}
        </div>
      </Modal>
    </>
  );
};

export default OfferLetter;
